import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Kpi, PageHeader, Section, Spinner } from "@/components/kit";
import { ResourceTable } from "@/components/ResourceTable";
import { useWsData } from "@/hooks/useWsData";
import { brl } from "@/lib/api";

const CLASSES = ["Caixa/Reserva", "Renda Fixa", "Ações", "FIIs", "Cripto", "Previdência", "Imóveis", "Veículos", "Empresa", "Outros"];
const INVEST = ["Caixa/Reserva", "Renda Fixa", "Ações", "FIIs", "Cripto", "Previdência"];
const COLORS = ["#D4AF37", "#10B981", "#3B82F6", "#F59E0B", "#8B5CF6", "#EC4899", "#14B8A6", "#EF4444", "#94A3B8", "#F5D77F"];
const FIELDS = [
  { name: "name", label: "Descrição", wide: true },
  { name: "asset_class", label: "Classe", type: "select", default: "Renda Fixa", options: CLASSES.map((v) => ({ value: v, label: v })) },
  { name: "value", label: "Valor atual (R$)", type: "number" },
  { name: "liquidity", label: "Liquidez", type: "select", default: "media", options: [{ value: "alta", label: "Alta" }, { value: "media", label: "Média" }, { value: "baixa", label: "Baixa" }] },
];

export default function Patrimonio() {
  const { data: rows, reload, wid } = useWsData("/assets");
  const { data: debts } = useWsData("/debts");
  if (!rows || !debts) return <Spinner />;
  const assets = rows.reduce((a, r) => a + r.value, 0);
  const liab = debts.reduce((a, r) => a + r.balance, 0);
  const inv = rows.filter((r) => INVEST.includes(r.asset_class)).reduce((a, r) => a + r.value, 0);
  const byClass = CLASSES.map((c) => ({ name: c, value: rows.filter((r) => r.asset_class === c).reduce((a, r) => a + r.value, 0) })).filter((x) => x.value);
  return (
    <div data-testid="patrimonio-page">
      <PageHeader eyebrow="Patrimônio" title="Ativos, passivos e investimentos" subtitle="Consolide seu patrimônio e acompanhe a diversificação por classe e liquidez." />
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Ativos" value={brl(assets)} tone="green" testid="assets-total" />
        <Kpi label="Passivos" value={brl(liab)} tone="red" testid="liabilities-total" />
        <Kpi label="Patrimônio líquido" value={brl(assets - liab)} tone="gold" testid="networth-total" />
        <Kpi label="Investimentos" value={brl(inv)} tone="blue" hint={assets ? `${((inv / assets) * 100).toFixed(0)}% dos ativos` : ""} testid="investments-total" />
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Section className="lg:col-span-2">
          <ResourceTable wid={wid} res="assets" title="Ativos" addLabel="Novo ativo" rows={rows} onChanged={reload} fields={FIELDS}
            columns={[{ key: "name", label: "Ativo" }, { key: "asset_class", label: "Classe" }, { key: "liquidity", label: "Liquidez" },
              { key: "value", label: "Valor", right: true, render: (r) => brl(r.value) }]} />
        </Section>
        <Section title="Por classe" testid="asset-class-chart">
          <div className="h-56">
            <ResponsiveContainer>
              <PieChart><Pie data={byClass} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85}>{byClass.map((_, i) => <Cell key={i} fill={COLORS[i]} stroke="none" />)}</Pie>
                <Tooltip contentStyle={{ background: "#0B1A33", border: "1px solid #1E3A6E" }} formatter={(v) => brl(v)} /></PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-2 space-y-1 text-xs">{byClass.map((c, i) => <li key={c.name} className="flex justify-between"><span><span className="mr-2 inline-block h-2 w-2 rounded-full" style={{ background: COLORS[i] }} />{c.name}</span><span className="num">{brl(c.value)}</span></li>)}</ul>
        </Section>
      </div>
    </div>
  );
}
